import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, Layers, Cpu, Zap, Move, Shield, Share2, FileText, 
  Mic, Play, ChevronDown, Sparkles, RotateCcw, Eye, Users, Award,
  Car, Sofa, Tv, UtensilsCrossed, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';

const AA_LOGO = '/aa-logo.png';

// Feature cards data
const features = [
  { icon: Layers, title: "3D Component Library", desc: "26+ modular interior components across 5 categories", color: "#8b5cf6" },
  { icon: Move, title: "Drag & Drop Engine", desc: "Snap-to-grid placement with collision detection", color: "#3b82f6" },
  { icon: Cpu, title: "AI Design Assistant", desc: "GPT-powered co-designer that optimizes your layout", color: "#10b981" },
  { icon: Mic, title: "Voice Control", desc: "Say 'Hey Archana' in 5 languages including Hindi & Tamil", color: "#f59e0b" },
  { icon: Shield, title: "Intelligent Alerts", desc: "Real-time warnings for legroom, weight & clearance", color: "#ef4444" },
  { icon: FileText, title: "Quote Generation", desc: "Generate branded quotations with GST & terms", color: "#06b6d4" },
];

// Stats data
const stats = [
  { value: "26+", label: "Components", icon: Sofa },
  { value: "10", label: "Pre-built Configs", icon: Car },
  { value: "5", label: "Languages", icon: Users },
  { value: "360°", label: "Interior View", icon: Eye },
];

// Configuration showcase data
const configurations = [
  { name: "Luxury 4-Seater", price: "₹4.85L", desc: "Premium travel experience", color: "#8b5cf6" },
  { name: "Family Camper", price: "₹3.95L", desc: "Adventure-ready", color: "#10b981" },
  { name: "Executive Office", price: "₹4.20L", desc: "Mobile workspace", color: "#3b82f6" },
  { name: "Election Campaign", price: "₹7.50L", desc: "Hydraulic platform lift", color: "#ef4444" },
];

// Interior view images with actual van interior photos
const interiorViews = [
  { 
    angle: 0, 
    label: "Luxury Captain Seats",
    image: "https://images.unsplash.com/photo-1698358894604-a2406c8715e6?w=800&q=80",
    desc: "Premium red leather seating"
  },
  { 
    angle: 45, 
    label: "Executive Lounge",
    image: "https://images.unsplash.com/photo-1716370287223-0a162e265ddc?w=800&q=80",
    desc: "Brown leather with wood accents"
  },
  { 
    angle: 90, 
    label: "Pantry Module",
    image: "https://images.unsplash.com/photo-1765371515325-b6611765dfad?w=800&q=80",
    desc: "Modern wood kitchen with marble"
  },
  { 
    angle: 135, 
    label: "Compact Washroom",
    image: "https://images.unsplash.com/photo-1655201403932-437c04a8aabd?w=800&q=80",
    desc: "Clean compact bathroom"
  },
  { 
    angle: 180, 
    label: "Kitchen & Storage",
    image: "https://images.unsplash.com/photo-1772475385491-f3cf64d4131a?w=800&q=80",
    desc: "Organized pantry shelves"
  },
  { 
    angle: 225, 
    label: "Comfort Zone",
    image: "https://images.unsplash.com/photo-1691157415383-9e85455a0f9e?w=800&q=80",
    desc: "Tan leather captain chairs"
  },
  { 
    angle: 270, 
    label: "Premium Interior",
    image: "https://images.unsplash.com/photo-1767277974735-a6e70cd71d2f?w=800&q=80",
    desc: "Quilted leather with ambient lighting"
  },
  { 
    angle: 315, 
    label: "Curved Wood Finish",
    image: "https://images.unsplash.com/photo-1765371514211-9b93c204bb81?w=800&q=80",
    desc: "Premium curved wood panels"
  },
];

export default function LandingPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [currentView, setCurrentView] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const heroRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mouse tracking for hero section
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left - rect.width / 2) / rect.width,
          y: (e.clientY - rect.top - rect.height / 2) / rect.height,
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Auto-rotate 360 view
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentView((prev) => (prev + 1) % interiorViews.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#020617] overflow-hidden" data-testid="landing-page">
      {/* Animated background */}
      <div className="fixed inset-0 z-0">
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            background: `radial-gradient(ellipse at ${50 + mousePosition.x * 10}% ${30 + mousePosition.y * 10}%, rgba(59,130,246,0.15) 0%, transparent 50%)`,
            transition: 'background 0.3s ease-out'
          }}
        />
        <div className="absolute inset-0 vehicle-grid-pattern opacity-20" />
        {/* Floating particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-blue-500/30 rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${5 + Math.random() * 10}s`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Nav */}
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrollY > 50 ? 'glass-panel backdrop-blur-xl' : 'bg-transparent'
        }`} 
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src={AA_LOGO} alt="Archana Automotives" className="h-10" />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-slate-400 hover:text-white transition-colors">Features</a>
            <a href="#360-view" className="text-sm text-slate-400 hover:text-white transition-colors">360° View</a>
            <a href="#configurations" className="text-sm text-slate-400 hover:text-white transition-colors">Configurations</a>
          </div>
          <div className="flex items-center gap-4">
            {user ? (
              <Button
                data-testid="nav-studio-btn"
                onClick={() => navigate('/studio')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-5"
              >
                Open Studio
              </Button>
            ) : (
              <Button
                data-testid="nav-login-btn"
                onClick={login}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-5"
              >
                Sign in with Google
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative min-h-screen flex items-center pt-16 px-6" 
        data-testid="hero-section"
      >
        <div className="relative max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Content */}
          <div className="z-10">
            <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 text-blue-400 text-xs font-jetbrains uppercase tracking-[0.2em] mb-6">
                <Sparkles className="w-3 h-3" />
                Force Motors Urbania
              </span>
            </div>

            <h1 className="font-chivo font-black text-5xl sm:text-6xl lg:text-7xl text-white tracking-tighter leading-[0.95] mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              REIMAGINE<br />
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                YOUR INTERIOR
              </span>
            </h1>

            <p className="text-slate-400 text-lg max-w-xl leading-relaxed mb-8 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              Design your perfect Force Urbania interior with AI-powered suggestions, 
              voice control in 5 languages, and stunning 3D visualization — all in your browser.
            </p>

            <div className="flex flex-wrap gap-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <Button
                data-testid="hero-get-started-btn"
                onClick={user ? () => navigate('/studio') : login}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 text-base gap-2 rounded-xl group"
              >
                {user ? 'Open Design Studio' : 'Start Designing Free'}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                data-testid="hero-demo-btn"
                variant="outline"
                size="lg"
                className="border-slate-700 text-slate-300 hover:bg-slate-800/50 px-8 py-6 text-base rounded-xl gap-2"
                onClick={() => document.getElementById('360-view')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Eye className="w-4 h-4" />
                View 360° Interior
              </Button>
            </div>

            {/* Voice hint */}
            <div className="mt-8 flex items-center gap-3 text-slate-500 text-sm animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800/50 border border-slate-700">
                <Mic className="w-3.5 h-3.5 text-purple-400" />
                <span>Say "Hey Archana" to get started</span>
              </div>
            </div>
          </div>

          {/* Right - Urbania Interior Preview */}
          <div className="relative animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <div 
              className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10"
              style={{
                transform: `perspective(1000px) rotateY(${mousePosition.x * 5}deg) rotateX(${-mousePosition.y * 5}deg)`,
                transition: 'transform 0.3s ease-out'
              }}
            >
              {/* Actual Urbania interior image */}
              <img 
                src="https://images.unsplash.com/photo-1668705815562-075161e1fb42?w=800&q=80" 
                alt="Luxury Van Interior"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
              
              {/* Floating component labels */}
              <div className="absolute top-6 left-6 px-3 py-2 rounded-lg bg-violet-500/30 border border-violet-500/40 backdrop-blur-md animate-float" style={{ animationDelay: '0s' }}>
                <div className="flex items-center gap-2">
                  <Sofa className="w-4 h-4 text-violet-300" />
                  <span className="text-xs text-white font-medium">Captain Seat</span>
                </div>
              </div>
              <div className="absolute top-1/3 right-6 px-3 py-2 rounded-lg bg-amber-500/30 border border-amber-500/40 backdrop-blur-md animate-float" style={{ animationDelay: '1s' }}>
                <div className="flex items-center gap-2">
                  <UtensilsCrossed className="w-4 h-4 text-amber-300" />
                  <span className="text-xs text-white font-medium">Pantry Station</span>
                </div>
              </div>
              <div className="absolute bottom-1/3 left-1/4 px-3 py-2 rounded-lg bg-emerald-500/30 border border-emerald-500/40 backdrop-blur-md animate-float" style={{ animationDelay: '2s' }}>
                <div className="flex items-center gap-2">
                  <Tv className="w-4 h-4 text-emerald-300" />
                  <span className="text-xs text-white font-medium">Entertainment</span>
                </div>
              </div>
              
              {/* Bottom label */}
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <span className="text-xs text-white/60 font-jetbrains uppercase tracking-widest">Force Urbania Interior</span>
              </div>
            </div>

            {/* Glow effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 blur-3xl -z-10 opacity-50" />
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-slate-600 text-xs uppercase tracking-widest">Scroll</span>
          <ChevronDown className="w-5 h-5 text-slate-600" />
        </div>
      </section>

      {/* Stats Bar */}
      <section className="relative z-10 border-y border-white/5 bg-slate-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <div 
              key={i} 
              className="text-center group cursor-default"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                <s.icon className="w-5 h-5 text-blue-400" />
              </div>
              <div className="font-chivo font-black text-3xl text-white mb-1 group-hover:text-blue-400 transition-colors">{s.value}</div>
              <div className="font-jetbrains text-xs text-slate-500 uppercase tracking-[0.2em]">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 360° View Section */}
      <section id="360-view" className="relative z-10 py-24 px-6" data-testid="360-view-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-jetbrains text-xs text-purple-400 uppercase tracking-[0.2em] mb-4 block">Immersive Experience</span>
            <h2 className="font-chivo font-bold text-4xl text-white tracking-tight mb-4">
              360° Interior View
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Explore every angle of the Force Urbania interior. Drag to rotate, zoom in to see details.
            </p>
          </div>

          {/* 360 Viewer */}
          <div className="relative max-w-4xl mx-auto">
            <div className="aspect-[16/9] rounded-2xl overflow-hidden border border-white/10">
              {/* Actual interior images */}
              <div className="absolute inset-0">
                {interiorViews.map((view, i) => (
                  <img
                    key={i}
                    src={view.image}
                    alt={view.label}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                      currentView === i ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-slate-900/30" />
              </div>
              
              {/* View label */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center">
                <div className="px-6 py-3 rounded-xl bg-slate-900/80 backdrop-blur-md border border-white/10">
                  <span className="text-lg text-white font-chivo font-semibold block">{interiorViews[currentView].label}</span>
                  <span className="text-xs text-slate-400">{interiorViews[currentView].desc}</span>
                </div>
              </div>

              {/* Rotation indicator */}
              <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/60 backdrop-blur-sm">
                <RotateCcw className="w-3.5 h-3.5 text-purple-400 animate-spin-slow" />
                <span className="text-xs text-slate-300">Auto-rotating</span>
              </div>

              {/* Navigation arrows */}
              <button 
                onClick={() => setCurrentView((prev) => (prev - 1 + interiorViews.length) % interiorViews.length)}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors backdrop-blur-sm border border-white/10"
                data-testid="360-prev-btn"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button 
                onClick={() => setCurrentView((prev) => (prev + 1) % interiorViews.length)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors backdrop-blur-sm border border-white/10"
                data-testid="360-next-btn"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* View selector dots */}
            <div className="flex justify-center gap-3 mt-6">
              {interiorViews.map((view, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentView(i)}
                  className={`h-2 rounded-full transition-all ${
                    currentView === i ? 'bg-purple-500 w-10' : 'bg-slate-600 hover:bg-slate-500 w-2'
                  }`}
                  data-testid={`360-dot-${i}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="relative z-10 py-24 px-6 bg-slate-900/30" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-jetbrains text-xs text-blue-400 uppercase tracking-[0.2em] mb-4 block">Capabilities</span>
            <h2 className="font-chivo font-bold text-4xl text-white tracking-tight mb-4">
              Everything You Need to Design
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Powerful tools to create your perfect Urbania interior, from AI assistance to voice control.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div
                key={i}
                className="glass-card rounded-xl p-6 hover:border-blue-500/30 transition-all duration-300 hover:-translate-y-1 group"
                data-testid={`feature-card-${i}`}
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors"
                  style={{ background: `${f.color}20` }}
                >
                  <f.icon className="w-6 h-6" style={{ color: f.color }} />
                </div>
                <h3 className="font-chivo font-semibold text-lg text-white mb-2">{f.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Configurations Showcase */}
      <section id="configurations" className="relative z-10 py-24 px-6" data-testid="configurations-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-jetbrains text-xs text-emerald-400 uppercase tracking-[0.2em] mb-4 block">Quick Start</span>
            <h2 className="font-chivo font-bold text-4xl text-white tracking-tight mb-4">
              Pre-built Configurations
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Start with a professionally designed layout and customize to your needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {configurations.map((config, i) => (
              <div
                key={i}
                className="glass-card rounded-xl p-6 hover:border-white/20 transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
                onClick={user ? () => navigate('/studio') : login}
                data-testid={`config-card-${i}`}
              >
                <div 
                  className="w-full h-32 rounded-lg mb-4 flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${config.color}20, ${config.color}10)` }}
                >
                  <Car className="w-16 h-16" style={{ color: `${config.color}60` }} />
                </div>
                <h3 className="font-chivo font-semibold text-white mb-1 group-hover:text-blue-400 transition-colors">{config.name}</h3>
                <p className="text-slate-500 text-sm mb-2">{config.desc}</p>
                <div className="flex items-center justify-between">
                  <span className="font-jetbrains text-lg text-blue-400">{config.price}</span>
                  <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-24 px-6 border-t border-white/5" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <div className="mb-8">
            <Award className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          </div>
          <h2 className="font-chivo font-bold text-4xl text-white mb-4">
            Ready to Reimagine Your Interior?
          </h2>
          <p className="text-slate-400 text-lg mb-8">
            Join hundreds of customers who have designed their perfect Urbania with Archana Automotives.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="cta-start-btn"
              onClick={user ? () => navigate('/studio') : login}
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-10 py-6 text-base gap-2 rounded-xl group"
            >
              {user ? 'Go to Studio' : 'Sign in & Start Designing'}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-slate-700 text-slate-300 hover:bg-slate-800/50 px-10 py-6 text-base rounded-xl"
              onClick={() => window.open('tel:+919876543210')}
            >
              Contact Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={AA_LOGO} alt="Archana Automotives" className="h-10" />
              </div>
              <p className="text-slate-500 text-sm">
                Your trusted partner for Force Motors Urbania interior customization in Chennai.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#360-view" className="hover:text-white transition-colors">360° View</a></li>
                <li><a href="#configurations" className="hover:text-white transition-colors">Configurations</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                <li>Chennai, Tamil Nadu</li>
                <li>+91 98765 43210</li>
                <li>info@archanaautomotives.com</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/5 pt-8 flex items-center justify-between text-slate-600 text-xs">
            <span>© 2026 Archana Automotives. All rights reserved.</span>
            <span>Force Motors Urbania Interior Configurator</span>
          </div>
        </div>
      </footer>

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(-360deg);
          }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
      `}</style>
    </div>
  );
}
